import React, { useState, useEffect } from 'react';
import { PlayIcon, BookOpenIcon, MagnifyingGlassPlusIcon, PauseIcon } from '@heroicons/react/24/solid';
import { audioService } from '../services/audioService';

const DevotionalButtons = ({ devotionalData, onFontSizeChange }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    // Check if audio is currently playing when component mounts
    setIsPlaying(audioService.isCurrentlyPlaying());

    // Cleanup on unmount
    return () => {
      // Optional: Stop audio on unmount if desired, or let it keep playing
      // audioService.stop(); 
    };
  }, []);

  const handlePlay = async () => {
    try {
      if (isPlaying && !isPaused) {
        // Currently playing -> pause it
        await audioService.pause();
        setIsPaused(true);
      } else if (isPaused) {
        // Currently paused -> resume it
        if (audioService.resume) {
          await audioService.resume();
        } else {
          // Fallback: resume via audio element
          if (audioService.audio) {
            await audioService.audio.play();
          } else if (audioService.speechSynthesis) {
            audioService.resumeSpeaking();
          }
        }
        setIsPaused(false);
      } else {
        // Not playing -> start playback
        console.log('Fetching audio for day:', devotionalData.day, 'Date:', devotionalData.date);
        const audioInfo = await audioService.getAudioUrl(devotionalData.day, devotionalData.date);

        // Play audio (or fallback to TTS if no MP3)
        const played = await audioService.play(audioInfo, devotionalData.devotionalContent);

        if (played) {
          setIsPlaying(true);
          setIsPaused(false);
        }
      }
    } catch (error) {
      console.error('Error handling playback:', error);
      setIsPlaying(false);
      setIsPaused(false);
    }
  };

  const handleReadChapter = () => {
    if (devotionalData?.chapter) {
      // Use NKJV as requested
      const url = `https://www.bible.com/bible/1/PRO.${devotionalData.chapter}.NKJV`;
      window.open(url, '_blank');
    }
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 my-8 w-full max-w-3xl mx-auto">
      {/* Read Chapter Button - Left on Desktop, Top on Mobile */}
      <button
        onClick={handleReadChapter}
        className="group flex flex-col items-center gap-2 min-w-[100px] transition-transform active:scale-95"
      >
        <div className="p-3 rounded-full bg-gray-100 text-gray-600 group-hover:bg-gray-200 group-hover:text-gray-800 transition-colors">
          <BookOpenIcon className="h-6 w-6" />
        </div>
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider group-hover:text-gray-700">
          Read Chapter {devotionalData?.chapter}
        </span>
      </button>

      {/* Play Button - Center */}
      <button
        onClick={handlePlay}
        className="group flex flex-col items-center gap-3 transition-transform active:scale-95"
      >
        <div className="p-5 rounded-full bg-black text-white shadow-lg group-hover:bg-gray-800 group-hover:shadow-xl transition-all duration-300 ring-4 ring-gray-100 group-hover:ring-gray-200">
          {isPlaying && !isPaused ? (
            <PauseIcon className="h-8 w-8 ml-0.5" />
          ) : (
            <PlayIcon className="h-8 w-8 ml-1" />
          )}
        </div>
        <span className="text-xs font-bold text-gray-900 uppercase tracking-widest">
          {isPlaying && !isPaused ? 'PAUSE' : 'PLAY'}
        </span>
      </button>

      {/* Larger Text Button - Right on Desktop, Bottom on Mobile */}
      <button
        onClick={onFontSizeChange}
        className="group flex flex-col items-center gap-2 min-w-[100px] transition-transform active:scale-95"
      >
        <div className="p-3 rounded-full bg-gray-100 text-gray-600 group-hover:bg-gray-200 group-hover:text-gray-800 transition-colors">
          <MagnifyingGlassPlusIcon className="h-6 w-6" />
        </div>
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider group-hover:text-gray-700">
          Larger Text
        </span>
      </button>
    </div>
  );
};

export default DevotionalButtons;